<template>
  <section class="px-6 py-6 space-y-6 min-h-screen font-light select-none text-left transition-colors duration-200"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-gray-50 text-gray-700'">

    <div class="flex justify-between items-center border-b pb-5 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-gray-200'">
      <div class="space-y-1">
        <h1 class="text-xl font-medium tracking-tight transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-gray-900'">Configuration Système</h1>
        <p class="text-xs font-light transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Gérez vos préférences de structure, vos flux de synchronisation, votre abonnement SaaS et vos passerelles de routage SMS.</p>
      </div>
      <button @click="saveAllSettings" :disabled="isSaving" class="px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-lg active:scale-[0.99] cursor-pointer focus:outline-none disabled:opacity-50"
        :class="isDark
          ? 'bg-white hover:bg-[#D5CCBC] text-black shadow-white/5'
          : 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-600/20'">
        {{ isSaving ? 'Sauvegarde...' : 'Sauvegarder les modifications' }}
      </button>
    </div>

    <div v-if="isLoading" class="flex justify-center py-20">
      <div class="text-zinc-500 text-sm">Chargement des paramètres...</div>
    </div>

    <div v-else class="flex lg:flex-row gap-6 items-start w-full">

      <nav class="lg:w-64 border rounded-2xl p-3 shrink-0 space-y-1 transition-colors duration-200"
        :class="isDark
          ? 'bg-zinc-900/20 border-zinc-900'
          : 'bg-white border-gray-200 shadow-sm'">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-xs font-medium uppercase tracking-wider transition-all duration-200 text-left border cursor-pointer focus:outline-none"
          :class="activeTab === tab.id
            ? isDark
              ? 'bg-zinc-900 text-white font-semibold border-zinc-800'
              : 'bg-gray-100 text-gray-900 font-semibold border-gray-300'
            : isDark
              ? 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/40 border-transparent'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0 text-current" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" :d="tab.iconPath" />
          </svg>
          <span>{{ tab.label }}</span>
        </button>
      </nav>

      <div class="flex-1 w-full space-y-6">

        <!-- PROFIL UTILISATEUR -->
        <div v-if="activeTab === 'profile'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Identité de l'Opérateur</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Informations personnelles du gestionnaire de la licence.</p>
          </div>

          <div class="flex items-center gap-5">
            <div class="relative group">
              <img
                :src="accountForm.photo || `https://ui-avatars.com/api/?background=27272a&color=fff&name=${accountForm.nom}+${accountForm.prenom}`"
                alt="Avatar"
                class="w-20 h-20 rounded-full object-cover border border-zinc-800 shadow-xl filter brightness-90 group-hover:brightness-50 transition-all duration-300"
              />
              <label class="absolute inset-0 flex items-center justify-center text-[10px] uppercase font-semibold text-white tracking-wider opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                Éditer
                <input type="file" @change="handleAvatarUpload" class="hidden" accept="image/*" />
              </label>
            </div>
            <div class="space-y-1 text-left">
              <p class="text-xs font-medium transition-colors duration-200"
                :class="isDark ? 'text-zinc-300' : 'text-gray-700'">Signature Numérique Profil</p>
              <p class="text-[11px] font-light transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Format JPEG ou PNG. Résolution recommandée : 400x400px.</p>
            </div>
          </div>

          <div class="grid md:grid-cols-2 gap-4">
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Nom de famille</label>
              <input v-model="accountForm.nom" type="text" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Prénom</label>
              <input v-model="accountForm.prenom" type="text" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Adresse Email</label>
              <input v-model="accountForm.email" type="email" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Téléphone Mobile</label>
              <input v-model="accountForm.telephone" type="tel" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
          </div>
        </div>

        <!-- INFORMATIONS SALON -->
        <div v-if="activeTab === 'salon'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Fiche Établissement</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Paramètres d'affichage publics de votre salon pour la vitrine client.</p>
          </div>

          <div class="grid md:grid-cols-2 gap-4">
            <div class="space-y-1.5 md:col-span-2">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Enseigne Commerciale</label>
              <input v-model="salonForm.nomEnseigne" type="text" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Adresse Physique</label>
              <input v-model="salonForm.adresse" type="text" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Code Postal & Ville</label>
              <input v-model="salonForm.ville" type="text" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Numéro SIRET</label>
              <input v-model="salonForm.siret" type="text" placeholder="000 000 000 00000" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs font-mono transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
            <div class="space-y-1.5">
              <label class="block text-[10px] uppercase tracking-widest font-medium font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Contact Public</label>
              <input v-model="salonForm.telephonePublic" type="tel" class="w-full px-4 py-2.5 rounded-xl border focus:outline-none text-xs transition-all"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-900/40 text-white focus:border-zinc-700 focus:bg-zinc-900/80'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-gray-400'" />
            </div>
          </div>

          <hr class="my-6 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'"/>

          <div>
            <h3 class="text-sm font-medium tracking-wide mb-4 transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Horaires d'Ouverture</h3>
            <div class="space-y-3">
              <div
                v-for="(slots, jour) in salonForm.horaires"
                :key="jour"
                class="grid md:grid-cols-4 gap-3 items-center p-3 border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-950/40 border-zinc-900/80'
                  : 'bg-gray-50 border-gray-200'"
              >
                <div class="text-xs font-medium capitalize font-mono tracking-wide transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-gray-900'">
                  {{ jour }}
                </div>

                <div v-if="slots.length > 0" class="md:col-span-3 space-y-2">
                  <div v-for="(slot, idx) in slots" :key="idx" class="flex items-center gap-2">
                    <input
                      type="time"
                      v-model="slot.debut"
                      class="px-2 py-1 rounded-lg border focus:outline-none font-mono text-xs transition-colors duration-200"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white'
                        : 'border-gray-300 bg-white text-gray-900'"
                    />
                    <span class="transition-colors duration-200"
                      :class="isDark ? 'text-zinc-600' : 'text-gray-400'">—</span>
                    <input
                      type="time"
                      v-model="slot.fin"
                      class="px-2 py-1 rounded-lg border focus:outline-none font-mono text-xs transition-colors duration-200"
                      :class="isDark
                        ? 'border-zinc-800 bg-zinc-900 text-white'
                        : 'border-gray-300 bg-white text-gray-900'"
                    />
                    <button
                      v-if="slots.length > 1"
                      @click="slots.splice(idx, 1)"
                      class="p-1.5 hover:text-red-400 transition-colors"
                      :class="isDark ? 'text-zinc-600' : 'text-gray-400'"
                      type="button"
                    >
                      <i class="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  <button
                    @click="salonForm.horaires[jour].push({ debut: '09:00', fin: '18:00' })"
                    class="text-xs hover:text-white transition-colors font-medium"
                    :class="isDark ? 'text-[#D5CCBC]' : 'text-amber-600'"
                    type="button"
                  >
                    + Ajouter un créneau
                  </button>
                </div>

                <div v-else class="md:col-span-3 flex items-center justify-between">
                  <span class="text-xs italic transition-colors duration-200"
                    :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Fermé</span>
                  <button
                    @click="salonForm.horaires[jour] = [{ debut: '09:00', fin: '18:00' }]"
                    class="px-3 py-1 text-xs border rounded-lg transition-colors"
                    :class="isDark
                      ? 'text-[#D5CCBC] hover:text-white border-zinc-800'
                      : 'text-amber-600 hover:text-amber-700 border-gray-300'"
                    type="button"
                  >
                    Ouvrir
                  </button>
                </div>
              </div>
            </div>
          </div>

          <hr class="my-6 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'"/>

          <!-- Section Périodes de fermeture -->
          <div>
            <h3 class="text-sm font-medium tracking-wide mb-4 transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Périodes de fermeture</h3>
            <p class="text-xs mb-4 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Gérez vos congés et fermetures exceptionnelles</p>

            <div class="space-y-3">
              <div
                v-for="(periode, index) in salonForm.vacances"
                :key="index"
                class="flex items-center gap-3 p-4 border rounded-lg transition-colors duration-200"
                :class="isDark
                  ? 'border-zinc-900 bg-zinc-950/40'
                  : 'border-gray-200 bg-gray-50'"
              >
                <div class="flex-1 grid grid-cols-3 gap-3">
                  <input
                    v-model="periode.titre"
                    type="text"
                    placeholder="Titre (ex: Vacances d'été)"
                    class="border rounded px-3 py-2 text-xs focus:outline-none focus:ring-2 transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:ring-zinc-700'
                      : 'border-gray-200 bg-white text-gray-900 focus:ring-amber-500'"
                  />
                  <input
                    v-model="periode.date_debut"
                    type="date"
                    class="border rounded px-3 py-2 text-xs focus:outline-none focus:ring-2 transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:ring-zinc-700'
                      : 'border-gray-200 bg-white text-gray-900 focus:ring-amber-500'"
                  />
                  <input
                    v-model="periode.date_fin"
                    type="date"
                    class="border rounded px-3 py-2 text-xs focus:outline-none focus:ring-2 transition-all"
                    :class="isDark
                      ? 'border-zinc-800 bg-zinc-900 text-white focus:ring-zinc-700'
                      : 'border-gray-200 bg-white text-gray-900 focus:ring-amber-500'"
                  />
                </div>
                <button
                  @click="salonForm.vacances.splice(index, 1)"
                  class="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  :class="isDark ? 'text-red-400' : 'text-red-500'"
                  type="button"
                >
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>

              <button
                @click="salonForm.vacances.push({ titre: '', date_debut: '', date_fin: '' })"
                class="w-full py-3 border-2 border-dashed rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-2"
                :class="isDark
                  ? 'border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:bg-zinc-900/40'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'"
                type="button"
              >
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                Ajouter une période de fermeture
              </button>
            </div>
          </div>

          <hr class="my-6 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'"/>

          <!-- Section Paiement : Prépaiement ou Accompte -->
          <div>
            <h3 class="text-sm font-medium tracking-wide mb-2 transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Politique de Paiement</h3>
            <p class="text-xs mb-4 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Choisissez si vous souhaitez activer le prépaiement complet ou demander un accompte par défaut</p>

            <div class="space-y-3">
              <!-- Option Prépaiement -->
              <div
                @click="salonForm.prepaiement_active = true; salonForm.acompte_demande_par_defaut = false; salonForm.prepaiement_optionnel = false"
                class="flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all duration-200"
                :class="salonForm.prepaiement_active
                  ? isDark
                    ? 'border-white/40 bg-zinc-900/60 ring-2 ring-white/20'
                    : 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                  : isDark
                    ? 'border-zinc-900 bg-zinc-950/40 hover:border-zinc-800'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'"
              >
                <div class="flex items-center justify-center w-5 h-5 rounded-full border-2 mt-0.5 transition-colors"
                  :class="salonForm.prepaiement_active
                    ? isDark
                      ? 'border-white bg-white'
                      : 'border-amber-600 bg-amber-600'
                    : isDark
                      ? 'border-zinc-700 bg-transparent'
                      : 'border-gray-300 bg-transparent'"
                >
                  <div v-if="salonForm.prepaiement_active" class="w-2 h-2 rounded-full"
                    :class="isDark ? 'bg-zinc-900' : 'bg-white'">
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-sm font-medium mb-1 transition-colors duration-200"
                    :class="salonForm.prepaiement_active
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : isDark ? 'text-zinc-400' : 'text-gray-700'">
                    Activer le Prépaiement Complet
                  </h4>
                  <p class="text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                    Le client devra payer l'intégralité de la prestation lors de la réservation en ligne
                  </p>
                </div>
              </div>

              <!-- Option Accompte -->
              <div
                @click="salonForm.prepaiement_active = false; salonForm.acompte_demande_par_defaut = true; salonForm.prepaiement_optionnel = false"
                class="flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all duration-200"
                :class="salonForm.acompte_demande_par_defaut
                  ? isDark
                    ? 'border-white/40 bg-zinc-900/60 ring-2 ring-white/20'
                    : 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                  : isDark
                    ? 'border-zinc-900 bg-zinc-950/40 hover:border-zinc-800'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'"
              >
                <div class="flex items-center justify-center w-5 h-5 rounded-full border-2 mt-0.5 transition-colors"
                  :class="salonForm.acompte_demande_par_defaut
                    ? isDark
                      ? 'border-white bg-white'
                      : 'border-amber-600 bg-amber-600'
                    : isDark
                      ? 'border-zinc-700 bg-transparent'
                      : 'border-gray-300 bg-transparent'"
                >
                  <div v-if="salonForm.acompte_demande_par_defaut" class="w-2 h-2 rounded-full"
                    :class="isDark ? 'bg-zinc-900' : 'bg-white'">
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-sm font-medium mb-1 transition-colors duration-200"
                    :class="salonForm.acompte_demande_par_defaut
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : isDark ? 'text-zinc-400' : 'text-gray-700'">
                    Demander un Accompte Global
                  </h4>
                  <p class="text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                    Le client devra verser un accompte (montant configuré par service ou global) lors de la réservation
                  </p>
                </div>
              </div>

              <!-- Option Prépaiement Optionnel -->
              <div
                @click="salonForm.prepaiement_optionnel = true; salonForm.prepaiement_active = false; salonForm.acompte_demande_par_defaut = false"
                class="flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all duration-200"
                :class="salonForm.prepaiement_optionnel
                  ? isDark
                    ? 'border-white/40 bg-zinc-900/60 ring-2 ring-white/20'
                    : 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                  : isDark
                    ? 'border-zinc-900 bg-zinc-950/40 hover:border-zinc-800'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'"
              >
                <div class="flex items-center justify-center w-5 h-5 rounded-full border-2 mt-0.5 transition-colors"
                  :class="salonForm.prepaiement_optionnel
                    ? isDark
                      ? 'border-white bg-white'
                      : 'border-amber-600 bg-amber-600'
                    : isDark
                      ? 'border-zinc-700 bg-transparent'
                      : 'border-gray-300 bg-transparent'"
                >
                  <div v-if="salonForm.prepaiement_optionnel" class="w-2 h-2 rounded-full"
                    :class="isDark ? 'bg-zinc-900' : 'bg-white'">
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-sm font-medium mb-1 transition-colors duration-200"
                    :class="salonForm.prepaiement_optionnel
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : isDark ? 'text-zinc-400' : 'text-gray-700'">
                    Prépaiement Optionnel
                  </h4>
                  <p class="text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                    Le client peut choisir de payer en ligne ou non lors de la réservation
                  </p>
                </div>
              </div>

              <!-- Option Désactivé -->
              <div
                @click="salonForm.prepaiement_optionnel = false; salonForm.prepaiement_active = false; salonForm.acompte_demande_par_defaut = false"
                class="flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all duration-200"
                :class="!salonForm.prepaiement_active && !salonForm.acompte_demande_par_defaut && !salonForm.prepaiement_optionnel
                  ? isDark
                    ? 'border-white/40 bg-zinc-900/60 ring-2 ring-white/20'
                    : 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                  : isDark
                    ? 'border-zinc-900 bg-zinc-950/40 hover:border-zinc-800'
                    : 'border-gray-200 bg-gray-50 hover:border-gray-300'"
              >
                <div class="flex items-center justify-center w-5 h-5 rounded-full border-2 mt-0.5 transition-colors"
                  :class="!salonForm.prepaiement_active && !salonForm.acompte_demande_par_defaut && !salonForm.prepaiement_optionnel
                    ? isDark
                      ? 'border-white bg-white'
                      : 'border-amber-600 bg-amber-600'
                    : isDark
                      ? 'border-zinc-700 bg-transparent'
                      : 'border-gray-300 bg-transparent'"
                >
                  <div v-if="!salonForm.prepaiement_active && !salonForm.acompte_demande_par_defaut && !salonForm.prepaiement_optionnel" class="w-2 h-2 rounded-full"
                    :class="isDark ? 'bg-zinc-900' : 'bg-white'">
                  </div>
                </div>
                <div class="flex-1">
                  <h4 class="text-sm font-medium mb-1 transition-colors duration-200"
                    :class="!salonForm.prepaiement_active && !salonForm.acompte_demande_par_defaut && !salonForm.prepaiement_optionnel
                      ? isDark ? 'text-white' : 'text-gray-900'
                      : isDark ? 'text-zinc-400' : 'text-gray-700'">
                    Aucun Paiement en Ligne
                  </h4>
                  <p class="text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                    Les clients peuvent réserver sans paiement en ligne (paiement sur place uniquement)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PROFIL PUBLIC -->
        <div v-if="activeTab === 'public-profile'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Profil Public</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Gérez les photos et la description de votre établissement.</p>
          </div>

           <div class="px-4 py-3 space-y-8 max-w-6xl">



    <!-- Section Photo Principale -->
    <div class="space-y-4">
      <div class="border-b pb-3">
        <h3 class="text-sm font-medium text-gray-900">Photo Principale</h3>
        <p class="text-xs text-gray-500 mt-1">Cette image sera affichée comme photo principale de votre établissement</p>
      </div>

      <div class="flex items-center gap-6">
        <div class="relative group">
          <img
            :src="salonForm.image || 'https://cnopt.tn/wp-content/uploads/2023/06/default-image.jpg'"
            alt="Logo établissement"
            class="w-32 h-32 rounded-2xl object-cover border-2 shadow-md transition-colors duration-200"
            :class="isDark ? 'border-zinc-800' : 'border-gray-200'"
          />
          <label class="absolute inset-0 flex items-center justify-center bg-black/60 rounded-2xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
            <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <input type="file" @change="uploadLogo" class="hidden" accept="image/*" />
          </label>
        </div>
        <div class="text-left space-y-2">
          <p class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Format recommandé : JPG, PNG</p>
          <p class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Taille minimale : 800x800px</p>
        </div>
      </div>
    </div>

    <hr />

    <!-- Section Galerie Photos -->
    <div class="space-y-4">
      <div class="border-b pb-3">
        <h3 class="text-sm font-medium text-gray-900">Galerie Photos</h3>
        <p class="text-xs text-gray-500 mt-1">Ajoutez jusqu'à 4 photos de votre établissement</p>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div
          v-for="(photo, index) in salonForm.photos"
          :key="index"
          class="relative aspect-square rounded-xl overflow-hidden border-2 group transition-colors duration-200"
          :class="isDark ? 'border-zinc-800' : 'border-gray-200'"
        >
          <img
            :src="photo"
            alt="Photo galerie"
            class="w-full h-full object-cover"
          />
          <button
            @click="removePhoto(index)"
            class="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <label
          v-if="salonForm.photos.length < 4"
          class="aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors"
          :class="isDark
            ? 'border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/40'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'"
        >
          <svg class="w-8 h-8 mb-2 transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-gray-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span class="text-xs transition-colors duration-200"
            :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Ajouter une photo</span>
          <input type="file" @change="uploadPhoto" class="hidden" accept="image/*" />
        </label>
      </div>
    </div>

    <hr class="transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-gray-200'"/>

    <!-- Section Description -->
    <div class="space-y-4">
      <div class="border-b pb-3 transition-colors duration-200"
        :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
        <h3 class="text-sm font-medium transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-gray-900'">Description de l'établissement</h3>
        <p class="text-xs mt-1 transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Présentez votre établissement à vos futurs clients</p>
      </div>

      <textarea
        v-model="salonForm.description"
        rows="6"
        placeholder="Décrivez votre établissement, vos services, votre équipe..."
        class="w-full border rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 resize-none transition-all"
        :class="isDark
          ? 'border-zinc-900 bg-zinc-900/40 text-white focus:ring-zinc-700 focus:bg-zinc-900/80'
          : 'border-gray-200 bg-white text-gray-900 focus:ring-amber-500'"
      ></textarea>
      <p class="text-xs text-right transition-colors duration-200"
        :class="isDark ? 'text-zinc-500' : 'text-gray-500'">{{ salonForm.description?.length || 0 }} caractères</p>
    </div>

  </div>
        </div>

        <!-- GOOGLE CALENDAR SYNC -->
        <div v-if="activeTab === 'google-sync'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Synchronisation Google Calendar</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Intégrez vos rendez-vous avec votre calendrier Google personnel.</p>
          </div>

          <div class="flex items-center justify-between p-5 bg-gradient-to-br border rounded-xl transition-colors duration-200"
            :class="isDark
              ? 'from-blue-950/20 via-purple-950/20 to-zinc-900/20 border-blue-900/30'
              : 'from-blue-50 via-purple-50 to-gray-50 border-blue-200'">
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 rounded-full border flex items-center justify-center transition-colors duration-200"
                :class="isDark
                  ? 'bg-blue-950/40 border-blue-900/40'
                  : 'bg-blue-100 border-blue-300'">
                <i class="fab fa-google text-xl transition-colors duration-200"
                  :class="isDark ? 'text-blue-400' : 'text-blue-600'"></i>
              </div>
              <div class="space-y-0.5 text-left">
                <p class="text-xs font-medium transition-colors duration-200"
                  :class="isDark ? 'text-white' : 'text-gray-900'">Synchronisation en temps réel</p>
                <p class="text-[11px] transition-colors duration-200"
                  :class="isDark ? 'text-zinc-500' : 'text-gray-500'">{{ connections.length }} calendrier(s) connecté(s)</p>
              </div>
            </div>

            <button
              type="button"
              @click="goToSyncPage"
              class="px-4 py-2 rounded-xl text-[10px] font-mono uppercase tracking-wider font-semibold transition-all cursor-pointer focus:outline-none flex items-center gap-2"
              :class="isDark
                ? 'bg-white hover:bg-[#D5CCBC] text-black'
                : 'bg-amber-600 hover:bg-amber-700 text-white'"
            >
              <i class="fas fa-cog"></i>
              Gérer les connexions
            </button>
          </div>

          <div v-if="connections.length > 0" class="space-y-3">
            <div
              v-for="conn in connections"
              :key="conn.id"
              class="flex items-center justify-between p-4 border rounded-xl transition-colors duration-200"
              :class="isDark
                ? 'bg-zinc-950/60 border-zinc-900'
                : 'bg-gray-50 border-gray-200'"
            >
              <div class="flex items-center gap-3">
                <div class="w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200"
                  :class="isDark
                    ? 'bg-blue-950/40 border border-blue-900/40'
                    : 'bg-blue-100 border border-blue-300'">
                  <i class="fab fa-google text-sm transition-colors duration-200"
                    :class="isDark ? 'text-blue-400' : 'text-blue-600'"></i>
                </div>
                <div class="text-left">
                  <p class="text-xs font-medium transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">{{ conn.accountName || conn.accountEmail }}</p>
                  <p class="text-[10px] transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-500'">{{ conn.calendarName }}</p>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider border"
                  :class="conn.syncEnabled
                    ? isDark
                      ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                      : 'bg-emerald-100 text-emerald-700 border-emerald-300'
                    : isDark
                      ? 'bg-zinc-900 text-zinc-600 border-zinc-800'
                      : 'bg-gray-100 text-gray-500 border-gray-300'"
                >
                  {{ conn.syncEnabled ? 'Actif' : 'Inactif' }}
                </span>
              </div>
            </div>
          </div>

          <div v-else class="border rounded-xl p-6 text-center transition-colors duration-200"
            :class="isDark
              ? 'bg-zinc-950/40 border-zinc-900'
              : 'bg-gray-50 border-gray-200'">
            <i class="fas fa-plug text-3xl mb-3 transition-colors duration-200"
              :class="isDark ? 'text-zinc-600' : 'text-gray-400'"></i>
            <p class="text-sm transition-colors duration-200"
              :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Aucun calendrier connecté</p>
            <p class="text-xs mt-1 transition-colors duration-200"
              :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Accédez à la page Import & Sync pour connecter votre agenda Google</p>
          </div>
        </div>

        <!-- ABONNEMENT STRIPE -->
        <div v-if="activeTab === 'stripe-billing'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Gestion de l'Abonnement Plateforme</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Supervisez votre forfait de facturation SaaS et gérez vos paiements.</p>
          </div>

          <div v-if="subscriptionLoading" class="flex justify-center py-12">
            <div class="text-sm transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Chargement...</div>
          </div>

          <div v-else class="space-y-6">
            <!-- Pas d'abonnement actif -->
            <div v-if="!subscriptionStatus.isActive" class="p-6 bg-gradient-to-br border rounded-xl space-y-4 transition-colors duration-200"
              :class="isDark
                ? 'from-purple-950/20 via-zinc-900/20 to-zinc-900/20 border-purple-900/30'
                : 'from-purple-50 via-pink-50 to-gray-50 border-purple-200'">
              <div class="flex items-start gap-3">
                <div class="w-10 h-10 border rounded-full flex items-center justify-center shrink-0 transition-colors duration-200"
                  :class="isDark
                    ? 'bg-purple-950/40 border-purple-900/40'
                    : 'bg-purple-100 border-purple-300'">
                  <i class="fas fa-rocket text-sm transition-colors duration-200"
                    :class="isDark ? 'text-purple-400' : 'text-purple-600'"></i>
                </div>
                <div class="space-y-2">
                  <h3 class="text-sm font-semibold transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">Aucun abonnement actif</h3>
                  <p class="text-xs leading-relaxed transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                    Choisissez un plan pour débloquer toutes les fonctionnalités de BookiPro et développer votre activité.
                  </p>
                  <div class="flex gap-2 pt-2">
                    <RouterLink
                      to="/subscription/plans"
                      class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors"
                      :class="isDark
                        ? 'bg-white hover:bg-[#D5CCBC] text-black'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'"
                    >
                      <i class="fas fa-star"></i>
                      Choisir un abonnement
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>

            <!-- Abonnement actif -->
            <div v-else-if="subscriptionStatus.isActive && subscriptionStatus.plan && subscriptionStatus.plan !== 'gratuit' && subscriptionStatus.status !== 'inactive'">
              <!-- Lifetime Plan -->
              <div v-if="subscriptionStatus.plan === 'lifetime'" class="p-5 bg-gradient-to-br border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'from-purple-950/20 via-pink-950/20 to-zinc-900/20 border-purple-900/30'
                  : 'from-purple-50 via-pink-50 to-gray-50 border-purple-200'">
                <div class="flex items-center justify-between">
                  <div class="space-y-2">
                    <div class="flex items-center gap-3">
                      <span class="px-2.5 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider border transition-colors duration-200"
                        :class="isDark
                          ? 'bg-gradient-to-r from-purple-950/60 to-pink-950/60 text-purple-300 border-purple-900/40'
                          : 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border-purple-300'">
                        <i class="fas fa-infinity mr-1"></i>
                        Lifetime
                      </span>
                      <span class="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border transition-colors duration-200"
                        :class="isDark
                          ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-300'">
                        Actif
                      </span>
                    </div>
                    <p class="text-xs transition-colors duration-200"
                      :class="isDark ? 'text-purple-300/80' : 'text-purple-600'">
                      <i class="fas fa-check-circle mr-1"></i>
                      Accès à vie - Aucun paiement récurrent
                    </p>
                    <p class="text-2xl font-mono font-bold transition-colors duration-200"
                      :class="isDark ? 'text-white' : 'text-gray-900'">
                      499€<span class="text-sm font-light transition-colors duration-200"
                        :class="isDark ? 'text-zinc-500' : 'text-gray-500'"> one-time</span>
                    </p>
                  </div>
                  <div class="flex flex-col gap-2">
                    <button
                      @click="openStripePortal"
                      :disabled="isOpeningPortal"
                      class="px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 whitespace-nowrap"
                      :class="isDark
                        ? 'bg-white hover:bg-[#D5CCBC] text-black'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'"
                    >
                      <i class="fas fa-file-invoice mr-2"></i>
                      {{ isOpeningPortal ? 'Chargement...' : 'Mes factures' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Regular Subscription -->
              <div v-else class="p-5 bg-gradient-to-br border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'from-emerald-950/20 via-zinc-900/20 to-zinc-900/20 border-emerald-900/30'
                  : 'from-emerald-50 via-teal-50 to-gray-50 border-emerald-200'">
                <div class="flex items-center justify-between">
                  <div class="space-y-2">
                    <div class="flex items-center gap-3">
                      <span class="px-2.5 py-1 rounded-lg text-xs font-semibold uppercase tracking-wider border transition-colors duration-200"
                        :class="isDark
                          ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-300'">
                        {{ subscriptionStatus.plan }}
                      </span>
                      <span class="px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border transition-colors duration-200"
                        :class="isDark
                          ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                          : 'bg-emerald-100 text-emerald-700 border-emerald-300'">
                        Actif
                      </span>
                    </div>
                    <p v-if="subscriptionStatus.currentPeriodEnd" class="text-xs transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                      Renouvellement le {{ formatDate(subscriptionStatus.currentPeriodEnd) }}
                    </p>
                    <p v-if="subscriptionStatus.amount" class="text-2xl font-mono font-bold transition-colors duration-200"
                      :class="isDark ? 'text-white' : 'text-gray-900'">
                      {{ subscriptionStatus.amount }}€<span class="text-sm font-light transition-colors duration-200"
                        :class="isDark ? 'text-zinc-500' : 'text-gray-500'">/mois</span>
                    </p>
                  </div>
                  <div class="flex flex-col gap-2">
                    <button
                      @click="openStripePortal"
                      :disabled="isOpeningPortal"
                      class="px-4 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors disabled:opacity-50 whitespace-nowrap"
                      :class="isDark
                        ? 'bg-white hover:bg-[#D5CCBC] text-black'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'"
                    >
                      <i class="fas fa-credit-card mr-2"></i>
                      {{ isOpeningPortal ? 'Chargement...' : 'Gérer mon abonnement' }}
                    </button>
                    <RouterLink
                      to="/subscription/plans"
                      class="inline-flex items-center justify-center gap-2 px-4 py-2.5 border rounded-xl text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap"
                      :class="isDark
                        ? 'bg-zinc-900 hover:bg-zinc-800 text-white border-zinc-800'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border-gray-300'"
                    >
                      <i class="fas fa-layer-group"></i>
                      Voir tous les plans
                    </RouterLink>
                  </div>
                </div>
              </div>

              <!-- Informations -->
              <div class="border rounded-xl p-4 space-y-2 mt-6 transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-950/40 border-zinc-900'
                  : 'bg-blue-50 border-blue-200'">
                <div class="flex items-start gap-2">
                  <i class="fas fa-info-circle text-sm mt-0.5 transition-colors duration-200"
                    :class="isDark ? 'text-blue-400' : 'text-blue-600'"></i>
                  <div class="text-xs space-y-1 transition-colors duration-200"
                    :class="isDark ? 'text-zinc-400' : 'text-gray-700'">
                    <p>Avec "Gérer mon abonnement" vous pouvez :</p>
                    <ul class="list-disc list-inside space-y-0.5 ml-2 text-[11px] transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                      <li>Modifier votre plan d'abonnement</li>
                      <li>Mettre à jour votre moyen de paiement</li>
                      <li>Télécharger vos factures</li>
                      <li>Annuler votre abonnement</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- CONFIGURATION SMS -->
        <div v-if="activeTab === 'sms-config'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Crédits SMS</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Gérez vos crédits pour l'envoi de SMS de confirmation et rappels.</p>
          </div>

          <div v-if="smsLoading" class="flex justify-center py-12">
            <div class="text-sm transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Chargement...</div>
          </div>

          <div v-else class="space-y-6">
            <!-- Solde actuel -->
            <div class="p-5 bg-gradient-to-br border rounded-xl transition-colors duration-200"
              :class="isDark
                ? 'from-purple-950/20 via-zinc-900/20 to-zinc-900/20 border-purple-900/30'
                : 'from-purple-50 via-pink-50 to-gray-50 border-purple-200'">
              <div class="flex items-center justify-between">
                <div class="space-y-2">
                  <p class="text-xs uppercase tracking-wider transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Solde disponible</p>
                  <p class="text-4xl font-mono font-bold transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">
                    {{ smsBalance.balance }}
                    <span class="text-sm font-light transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-gray-500'">SMS</span>
                  </p>
                  <div class="flex gap-4 text-xs transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                    <span>Total acheté : {{ smsBalance.totalPurchased }}</span>
                    <span>•</span>
                    <span>Total utilisé : {{ smsBalance.totalUsed }}</span>
                  </div>
                </div>
                <div
                  class="w-16 h-16 rounded-full border-4 flex items-center justify-center transition-colors duration-200"
                  :class="smsBalance.balance > 50
                    ? isDark
                      ? 'border-emerald-500/30 bg-emerald-950/40'
                      : 'border-emerald-500/50 bg-emerald-100'
                    : smsBalance.balance > 10
                      ? isDark
                        ? 'border-amber-500/30 bg-amber-950/40'
                        : 'border-amber-500/50 bg-amber-100'
                      : isDark
                        ? 'border-red-500/30 bg-red-950/40'
                        : 'border-red-500/50 bg-red-100'"
                >
                  <i class="fas fa-sms text-2xl transition-colors duration-200"
                    :class="smsBalance.balance > 50
                      ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                      : smsBalance.balance > 10
                        ? isDark ? 'text-amber-400' : 'text-amber-600'
                        : isDark ? 'text-red-400' : 'text-red-600'"></i>
                </div>
              </div>
            </div>

            <!-- Recharge automatique -->
            <div class="p-5 border rounded-xl transition-colors duration-200"
              :class="isDark
                ? 'bg-gradient-to-br from-amber-950/10 to-zinc-900/10 border-amber-900/30'
                : 'bg-gradient-to-br from-amber-50/50 to-orange-50/30 border-amber-200'">
              <div class="space-y-4">
                <div class="flex items-start justify-between">
                  <div class="space-y-1">
                    <div class="flex items-center gap-2">
                      <h3 class="text-sm font-medium transition-colors duration-200"
                        :class="isDark ? 'text-white' : 'text-gray-900'">Recharge automatique</h3>
                      <span class="px-2 py-0.5 text-[9px] font-bold uppercase rounded transition-colors duration-200"
                        :class="isDark ? 'bg-amber-950/40 text-amber-400' : 'bg-amber-100 text-amber-700'">
                        Recommandé
                      </span>
                    </div>
                    <p class="text-[11px] font-light transition-colors duration-200"
                      :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                      Rechargez automatiquement 100 SMS quand votre solde atteint 0. Plus de risque de manquer de crédits !
                    </p>
                  </div>
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input
                      v-model="autoRechargeEnabled"
                      @change="toggleAutoRecharge"
                      type="checkbox"
                      class="sr-only peer"
                    >
                    <div class="w-11 h-6 rounded-full peer transition-colors duration-200
                      peer-checked:after:translate-x-full peer-checked:after:border-white
                      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                      after:bg-white after:border-gray-300 after:border after:rounded-full
                      after:h-5 after:w-5 after:transition-all"
                      :class="isDark
                        ? 'bg-zinc-700 peer-checked:bg-amber-500'
                        : 'bg-gray-300 peer-checked:bg-amber-600'">
                    </div>
                  </label>
                </div>

                <div v-if="autoRechargeEnabled" class="pt-3 border-t space-y-2 transition-colors duration-200"
                  :class="isDark ? 'border-zinc-800' : 'border-amber-100'">
                  <div class="flex items-center gap-2 text-[11px] transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                    <i class="fas fa-info-circle"></i>
                    <span>100 SMS seront automatiquement rechargés pour 10€ TTC quand votre solde atteint 0</span>
                  </div>
                  <div class="flex items-center gap-2 text-[11px] transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                    <i class="fas fa-credit-card"></i>
                    <span>Le paiement sera effectué avec votre carte enregistrée</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Packages d'achat -->
            <div class="space-y-3">
              <h3 class="text-xs font-medium uppercase tracking-wider transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Recharger vos crédits manuellement</h3>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  v-for="pkg in smsPackages"
                  :key="pkg.id"
                  @click="purchaseSmsPackage(pkg.id)"
                  :disabled="isPurchasing"
                  class="p-4 border rounded-xl text-left transition-colors group disabled:opacity-50"
                  :class="isDark
                    ? 'bg-zinc-950/60 border-zinc-900 hover:border-purple-900/40'
                    : 'bg-gray-50 border-gray-200 hover:border-purple-400'"
                >
                  <div class="space-y-2">
                    <div class="flex items-center justify-between">
                      <span class="text-xs font-medium transition-colors duration-200"
                        :class="isDark ? 'text-white' : 'text-gray-900'">{{ pkg.credits }} SMS</span>
                      <i class="fas fa-arrow-right opacity-0 group-hover:opacity-100 transition-opacity"
                        :class="isDark ? 'text-purple-400' : 'text-purple-600'"></i>
                    </div>
                    <p class="text-2xl font-mono font-bold transition-colors duration-200"
                      :class="isDark ? 'text-white' : 'text-gray-900'">{{ pkg.price }}€</p>
                    <p class="text-[10px] transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-gray-600'">{{ (pkg.price / pkg.credits).toFixed(3) }}€ / SMS</p>
                  </div>
                </button>
              </div>
            </div>

            <!-- Configuration -->
            <div class="pt-4 border-t space-y-4 transition-colors duration-200"
              :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <h3 class="text-xs font-medium uppercase tracking-wider transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Configuration de l'envoi</h3>
              <div class="flex items-center justify-between p-4 rounded-xl border transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-900/40 border-zinc-900'
                  : 'bg-gray-50 border-gray-200'">
                <div class="space-y-0.5 text-left">
                  <p class="text-xs font-medium transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">SMS de Confirmation RDV</p>
                  <p class="text-[11px] font-light transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Envoi automatique après création de rendez-vous</p>
                </div>
                <input v-model="smsForm.sendConfirmation"
                  @change="saveSmsSettings"
                  type="checkbox"
                  class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                    : 'border-gray-300 bg-white accent-amber-600'" />
              </div>

              <div class="flex items-center justify-between p-4 rounded-xl border transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-900/40 border-zinc-900'
                  : 'bg-gray-50 border-gray-200'">
                <div class="space-y-0.5 text-left">
                  <p class="text-xs font-medium transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">SMS de Rappel (24h avant)</p>
                  <p class="text-[11px] font-light transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Notification envoyée la veille du rendez-vous</p>
                </div>
                <input v-model="smsForm.sendReminder"
                  @change="saveSmsSettings"
                  type="checkbox"
                  class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                    : 'border-gray-300 bg-white accent-amber-600'" />
              </div>

              <!-- SMS Annulation -->
              <div class="flex items-center justify-between p-4 border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-900/10 border-zinc-900'
                  : 'bg-gray-50 border-gray-200'">
                <div class="space-y-0.5 text-left">
                  <p class="text-xs font-medium transition-colors duration-200"
                    :class="isDark ? 'text-white' : 'text-gray-900'">SMS d'Annulation</p>
                  <p class="text-[11px] font-light transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Notifier le client lors d'une annulation de rendez-vous</p>
                </div>
                <input v-model="smsForm.sendCancellation"
                  @change="saveSmsSettings"
                  type="checkbox"
                  class="h-4 w-4 rounded cursor-pointer transition-colors duration-200"
                  :class="isDark
                    ? 'border-zinc-800 bg-zinc-900 accent-zinc-200'
                    : 'border-gray-300 bg-white accent-amber-600'" />
              </div>
            </div>
          </div>
        </div>

        <!-- STRIPE CONNECT - PAIEMENTS -->
        <div v-if="activeTab === 'stripe-connect'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Gestion des Paiements</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Connectez votre compte Stripe pour recevoir les paiements de vos clients directement sur votre compte bancaire.</p>
          </div>

          <div v-if="stripeConnectLoading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 transition-colors duration-200"
              :class="isDark ? 'border-white' : 'border-gray-900'"></div>
          </div>

          <div v-else class="space-y-6">
            <!-- Statut de connexion Stripe -->
            <div v-if="stripeConnectStatus.connected" class="border rounded-xl p-5 space-y-4 transition-colors duration-200"
              :class="isDark
                ? 'bg-emerald-950/20 border-emerald-900/40'
                : 'bg-emerald-50 border-emerald-200'">
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center w-12 h-12 rounded-xl transition-colors duration-200"
                  :class="isDark
                    ? 'bg-emerald-950/40'
                    : 'bg-emerald-100'">
                  <i class="fas fa-check-circle text-xl transition-colors duration-200"
                    :class="isDark ? 'text-emerald-400' : 'text-emerald-600'"></i>
                </div>
                <div class="flex-1 text-left">
                  <p class="text-sm font-medium transition-colors duration-200"
                    :class="isDark ? 'text-emerald-400' : 'text-emerald-700'">Compte Stripe connecté</p>
                  <p class="text-[11px] transition-colors duration-200"
                    :class="isDark ? 'text-emerald-500/80' : 'text-emerald-600'">
                    Vous pouvez recevoir les paiements de vos clients
                  </p>
                </div>
              </div>

              <div class="pt-3 border-t space-y-2 text-xs transition-colors duration-200"
                :class="isDark ? 'border-emerald-900/40' : 'border-emerald-200'">
                <div class="flex justify-between">
                  <span :class="isDark ? 'text-emerald-500/80' : 'text-emerald-600'">ID Compte Stripe:</span>
                  <span class="font-mono font-medium" :class="isDark ? 'text-emerald-400' : 'text-emerald-700'">
                    {{ stripeConnectStatus.accountId }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Pas de compte connecté -->
            <div v-else class="border rounded-xl p-6 space-y-4 text-center transition-colors duration-200"
              :class="isDark
                ? 'bg-amber-950/10 border-amber-900/30'
                : 'bg-amber-50/50 border-amber-200/60'">
              <div class="flex justify-center">
                <div class="flex items-center justify-center w-16 h-16 rounded-xl transition-colors duration-200"
                  :class="isDark
                    ? 'bg-amber-950/30'
                    : 'bg-amber-100'">
                  <i class="fas fa-exclamation-triangle text-2xl transition-colors duration-200"
                    :class="isDark ? 'text-amber-400' : 'text-amber-600'"></i>
                </div>
              </div>

              <div class="space-y-2">
                <h3 class="text-sm font-medium transition-colors duration-200"
                  :class="isDark ? 'text-amber-400' : 'text-amber-700'">
                  Compte Stripe non connecté
                </h3>
                <p class="text-xs transition-colors duration-200"
                  :class="isDark ? 'text-amber-500/80' : 'text-amber-600'">
                  Vous devez connecter un compte Stripe pour recevoir les paiements de vos clients. Les paiements en ligne ne fonctionneront pas tant que vous n'aurez pas configuré votre compte.
                </p>
              </div>

              <button
                @click="createStripeAccount"
                :disabled="isCreatingStripeAccount"
                class="px-6 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                :class="isDark
                  ? 'bg-white hover:bg-zinc-200 text-black'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'">
                <i class="fas fa-link mr-2"></i>
                {{ isCreatingStripeAccount ? 'Connexion en cours...' : 'Connecter mon compte Stripe' }}
              </button>
            </div>

            <!-- Informations importantes -->
            <div class="border-t pt-4 space-y-3 transition-colors duration-200"
              :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
              <h3 class="text-xs font-medium uppercase tracking-wider transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Pourquoi connecter Stripe ?</h3>
              <ul class="space-y-2 text-xs transition-colors duration-200"
                :class="isDark ? 'text-zinc-400' : 'text-gray-600'">
                <li class="flex items-start gap-2">
                  <i class="fas fa-check text-emerald-500 mt-0.5"></i>
                  <span>Recevez les paiements de vos clients directement sur votre compte bancaire</span>
                </li>
                <li class="flex items-start gap-2">
                  <i class="fas fa-check text-emerald-500 mt-0.5"></i>
                  <span>Les paiements sont transférés automatiquement tous les jours</span>
                </li>
                <li class="flex items-start gap-2">
                  <i class="fas fa-check text-emerald-500 mt-0.5"></i>
                  <span>Sécurisé par Stripe, leader mondial des paiements en ligne</span>
                </li>
                <li class="flex items-start gap-2">
                  <i class="fas fa-check text-emerald-500 mt-0.5"></i>
                  <span>Suivi en temps réel de vos revenus</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- PARRAINAGE -->
        <div v-if="activeTab === 'referral'" class="border rounded-2xl p-6 space-y-6 transition-colors duration-200"
          :class="isDark
            ? 'bg-zinc-900/20 border-zinc-900'
            : 'bg-white border-gray-200 shadow-sm'">
          <div class="border-b pb-4 transition-colors duration-200"
            :class="isDark ? 'border-zinc-900' : 'border-gray-200'">
            <h2 class="text-sm font-medium tracking-wide transition-colors duration-200"
              :class="isDark ? 'text-white' : 'text-gray-900'">Système de Parrainage</h2>
            <p class="text-[11px] font-light mt-0.5 transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Parrainez des professionnels et gagnez des mois d'abonnement gratuits.</p>
          </div>

          <div v-if="referralLoading" class="flex justify-center py-12">
            <div class="text-sm transition-colors duration-200"
              :class="isDark ? 'text-zinc-500' : 'text-gray-500'">Chargement...</div>
          </div>

          <div v-else class="space-y-6">
            <!-- Votre code de parrainage -->
            <div class="p-5 bg-gradient-to-br border rounded-xl transition-colors duration-200"
              :class="isDark
                ? 'from-amber-950/20 via-orange-950/20 to-zinc-900/20 border-amber-900/30'
                : 'from-amber-50 via-orange-50 to-gray-50 border-amber-200'">
              <div class="space-y-4">
                <div class="flex items-center gap-2">
                  <div class="w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-200"
                    :class="isDark
                      ? 'bg-amber-950/40 border-amber-900/40'
                      : 'bg-amber-100 border-amber-300'">
                    <i class="fas fa-gift text-lg transition-colors duration-200"
                      :class="isDark ? 'text-amber-400' : 'text-amber-600'"></i>
                  </div>
                  <div>
                    <h3 class="text-sm font-semibold transition-colors duration-200"
                      :class="isDark ? 'text-white' : 'text-gray-900'">Votre Code de Parrainage</h3>
                    <p class="text-xs transition-colors duration-200"
                      :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Partagez ce code pour gagner des mois gratuits</p>
                  </div>
                </div>

                <div class="flex gap-3">
                  <div class="flex-1 relative">
                    <input
                      :value="referralData.referralCode"
                      readonly
                      class="w-full px-4 py-3 rounded-xl border font-mono text-lg font-bold tracking-widest text-center uppercase transition-all cursor-pointer select-all"
                      :class="isDark
                        ? 'border-amber-900/40 bg-amber-950/40 text-amber-300'
                        : 'border-amber-300 bg-amber-100 text-amber-700'"
                      @click="copyReferralCode"
                    />
                  </div>
                  <button
                    @click="copyReferralCode"
                    class="px-6 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all active:scale-[0.98] flex items-center gap-2"
                    :class="isDark
                      ? 'bg-white hover:bg-[#D5CCBC] text-black'
                      : 'bg-amber-600 hover:bg-amber-700 text-white'"
                  >
                    <i class="fas fa-copy"></i>
                    Copier
                  </button>
                </div>

                <!-- Boutons de partage -->
                <div class="flex flex-wrap gap-2 pt-2">
                  <button
                    @click="shareReferralCode('email')"
                    class="px-4 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2"
                    :class="isDark
                      ? 'bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-800'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'"
                  >
                    <i class="fas fa-envelope"></i>
                    Email
                  </button>
                  <button
                    @click="shareReferralCode('whatsapp')"
                    class="px-4 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <i class="fab fa-whatsapp"></i>
                    WhatsApp
                  </button>
                  <button
                    @click="shareReferralCode('facebook')"
                    class="px-4 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <i class="fab fa-facebook"></i>
                    Facebook
                  </button>
                  <button
                    @click="shareReferralCode('twitter')"
                    class="px-4 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white"
                  >
                    <i class="fab fa-twitter"></i>
                    Twitter
                  </button>
                </div>
              </div>
            </div>

            <!-- Statistiques -->
            <div class="grid grid-cols-3 gap-4">
              <div class="p-4 border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'bg-emerald-950/20 border-emerald-900/40'
                  : 'bg-emerald-50 border-emerald-200'">
                <div class="space-y-1 text-center">
                  <p class="text-2xl font-bold transition-colors duration-200"
                    :class="isDark ? 'text-emerald-400' : 'text-emerald-700'">
                    {{ referralData.referralStats.freeMonthsEarned }}
                  </p>
                  <p class="text-xs uppercase tracking-wider font-medium transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Mois Gagnés</p>
                </div>
              </div>

              <div class="p-4 border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'bg-blue-950/20 border-blue-900/40'
                  : 'bg-blue-50 border-blue-200'">
                <div class="space-y-1 text-center">
                  <p class="text-2xl font-bold transition-colors duration-200"
                    :class="isDark ? 'text-blue-400' : 'text-blue-700'">
                    {{ referralData.referralStats.freeMonthsRemaining }}
                  </p>
                  <p class="text-xs uppercase tracking-wider font-medium transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Mois Restants</p>
                </div>
              </div>

              <div class="p-4 border rounded-xl transition-colors duration-200"
                :class="isDark
                  ? 'bg-purple-950/20 border-purple-900/40'
                  : 'bg-purple-50 border-purple-200'">
                <div class="space-y-1 text-center">
                  <p class="text-2xl font-bold transition-colors duration-200"
                    :class="isDark ? 'text-purple-400' : 'text-purple-700'">
                    {{ referralData.totalReferrals }}
                  </p>
                  <p class="text-xs uppercase tracking-wider font-medium transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">Total Parrainés</p>
                </div>
              </div>
            </div>

            <!-- Liste des parrainages -->
            <div class="space-y-3">
              <h3 class="text-xs font-medium uppercase tracking-wider transition-colors duration-200"
                :class="isDark ? 'text-white' : 'text-gray-900'">Vos Parrainages</h3>

              <div v-if="referralData.referredUsers.length === 0" class="border rounded-xl p-6 text-center transition-colors duration-200"
                :class="isDark
                  ? 'bg-zinc-950/40 border-zinc-900'
                  : 'bg-gray-50 border-gray-200'">
                <i class="fas fa-user-friends text-3xl mb-3 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-600' : 'text-gray-400'"></i>
                <p class="text-sm transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-600'">Aucun parrainage pour le moment</p>
                <p class="text-xs mt-1 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-600' : 'text-gray-500'">Partagez votre code pour commencer à gagner des mois gratuits !</p>
              </div>

              <div v-else class="space-y-2">
                <div
                  v-for="(user, index) in referralData.referredUsers"
                  :key="index"
                  class="flex items-center justify-between p-4 border rounded-xl transition-colors duration-200"
                  :class="isDark
                    ? 'bg-zinc-950/60 border-zinc-900'
                    : 'bg-gray-50 border-gray-200'"
                >
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-200"
                      :class="isDark
                        ? 'bg-purple-950/40 border-purple-900/40'
                        : 'bg-purple-100 border-purple-300'">
                      <i class="fas fa-building text-sm transition-colors duration-200"
                        :class="isDark ? 'text-purple-400' : 'text-purple-600'"></i>
                    </div>
                    <div class="text-left">
                      <p class="text-xs font-medium transition-colors duration-200"
                        :class="isDark ? 'text-white' : 'text-gray-900'">{{ user.companyName }}</p>
                      <p class="text-[10px] transition-colors duration-200"
                        :class="isDark ? 'text-zinc-500' : 'text-gray-500'">
                        Inscrit le {{ formatDate(user.registrationDate) }}
                      </p>
                    </div>
                  </div>
                  <span
                    class="px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-wider border"
                    :class="user.status === 'active'
                      ? isDark
                        ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40'
                        : 'bg-emerald-100 text-emerald-700 border-emerald-300'
                      : isDark
                        ? 'bg-zinc-900 text-zinc-600 border-zinc-800'
                        : 'bg-gray-100 text-gray-500 border-gray-300'"
                  >
                    {{ user.status === 'active' ? 'Actif' : 'Inactif' }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Info -->
            <div class="border rounded-xl p-4 space-y-2 transition-colors duration-200"
              :class="isDark
                ? 'bg-blue-950/20 border-blue-900/40'
                : 'bg-blue-50 border-blue-200'">
              <div class="flex items-start gap-2">
                <i class="fas fa-info-circle text-sm mt-0.5 transition-colors duration-200"
                  :class="isDark ? 'text-blue-400' : 'text-blue-600'"></i>
                <div class="text-xs space-y-1 transition-colors duration-200"
                  :class="isDark ? 'text-zinc-400' : 'text-gray-700'">
                  <p class="font-medium">Comment fonctionne le parrainage ?</p>
                  <ul class="list-disc list-inside space-y-0.5 ml-2 text-[11px] transition-colors duration-200"
                    :class="isDark ? 'text-zinc-500' : 'text-gray-600'">
                    <li>Partagez votre code unique avec d'autres professionnels</li>
                    <li>Lorsqu'ils s'inscrivent avec votre code, vous gagnez <strong>1 mois gratuit</strong></li>
                    <li>Les mois gratuits s'accumulent et sont utilisés automatiquement</li>
                    <li>Pas de limite ! Parrainez autant de personnes que vous voulez</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { useTheme } from '@/composables/useTheme'
import { usePermissions } from '@/composables/usePermissions'
import api from '@/api'

const route = useRoute()
const router = useRouter()

// === Thème ===
const { isDark } = useTheme()
const { isAdmin } = usePermissions()

const isLoading = ref(true)
const isSaving = ref(false)
const activeTab = ref('profile')

// Google Calendar
const connections = ref([])

// Subscription
const subscriptionLoading = ref(false)
const subscriptionStatus = ref({
  isActive: false,
  plan: 'gratuit',
  status: 'inactive',
  currentPeriodEnd: null,
  amount: null
})
const isOpeningPortal = ref(false)

// SMS Credits
const smsLoading = ref(false)
const smsBalance = ref({
  balance: 0,
  totalPurchased: 0,
  totalUsed: 0
})
const isPurchasing = ref(false)
const smsPackages = [
  { id: 'small', credits: 100, price: 10 },
  { id: 'medium', credits: 500, price: 45 },
  { id: 'large', credits: 1000, price: 80 },
  { id: 'enterprise', credits: 5000, price: 350 }
]

// Auto Recharge
const autoRechargeEnabled = ref(true)
const autoRechargeAmount = ref(100)
const autoRechargePackage = ref('small')

// Stripe Connect
const stripeConnectLoading = ref(false)
const stripeConnectStatus = ref({
  connected: false,
  accountId: null
})
const isCreatingStripeAccount = ref(false)

// Referral System
const referralLoading = ref(false)
const referralData = ref({
  referralCode: '',
  referredBy: null,
  referralStats: {
    freeMonthsEarned: 0,
    freeMonthsUsed: 0,
    freeMonthsRemaining: 0
  },
  referredUsers: [],
  totalReferrals: 0
})

// Définir tous les onglets disponibles avec les rôles autorisés
const allTabs = [
  { id: 'profile', label: 'Profil', iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z', adminOnly: false },
  { id: 'salon', label: 'Salon', iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', adminOnly: true },
  { id: 'public-profile', label: 'Profil Public', iconPath: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z', adminOnly: true },
  { id: 'google-sync', label: 'Google Sync', iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', adminOnly: false },
  { id: 'stripe-billing', label: 'Abonnement', iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z', adminOnly: true },
  { id: 'stripe-connect', label: 'Paiements', iconPath: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z', adminOnly: true },
  { id: 'sms-config', label: 'SMS Config', iconPath: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z', adminOnly: true },
  { id: 'referral', label: 'Parrainage', iconPath: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', adminOnly: false }
]

// Filtrer les onglets selon le rôle de l'utilisateur
const tabs = computed(() => {
  return allTabs.filter(tab => {
    if (tab.adminOnly) {
      return isAdmin.value
    }
    return true
  })
})

const accountForm = reactive({
  nom: '',
  prenom: '',
  email: '',
  telephone: '',
  photo: ''
})

const salonForm = reactive({
  nomEnseigne: '',
  adresse: '',
  ville: '',
  siret: '',
  telephonePublic: '',
  horaires: {
    lundi: [],
    mardi: [],
    mercredi: [],
    jeudi: [],
    vendredi: [],
    samedi: [],
    dimanche: []
  },
  vacances: [],
  image: '',
  photos: [],
  description: '',
  prepaiement_active: false,
  acompte_demande_par_defaut: false,
  prepaiement_optionnel: false
})



const smsForm = reactive({
  accountSid: '',
  authToken: '',
  phoneNumber: '',
  sendConfirmation: true,
  sendReminder: true,
  sendCancellation: false
})

onMounted(async () => {
  await loadAllSettings()
  await loadGoogleConnections()
  await loadSubscriptionStatus()
  await loadSmsBalance()
  await loadReferralInfo()
  await loadStripeConnectStatus()

  // Vérifier si on revient d'un achat SMS
  if (route.query.sms_purchase === 'success') {
    await verifySmsPurchaseFromUrl()
  }

  // Vérifier si on revient de l'onboarding Stripe Connect
  if (route.query.stripe_connect === 'success') {
    activeTab.value = 'stripe-connect'
    // Recharger le statut Stripe
    await loadStripeConnectStatus()
    toast.success('Compte Stripe connecté avec succès ! Vous pouvez maintenant recevoir les paiements.', {
      position: 'top-right',
      autoClose: 5000
    })
    // Nettoyer l'URL
    router.replace({ query: {} })
  } else if (route.query.stripe_connect === 'refresh') {
    activeTab.value = 'stripe-connect'
    await loadStripeConnectStatus()
    toast.info('Veuillez compléter la configuration de votre compte Stripe', {
      position: 'top-right',
      autoClose: 3000
    })
    router.replace({ query: {} })
  }
})

const loadAllSettings = async () => {
  try {
    isLoading.value = true

    // Load admin profile
    const profileResponse = await api.auth.getProfile()
    const adminData = profileResponse.data.admin || profileResponse.data
    console.log('👤 Données Admin chargées:', adminData)

    Object.assign(accountForm, {
      nom: adminData.nom || '',
      prenom: adminData.prenom || '',
      email: adminData.email || '',
      telephone: adminData.telephone || '',
      photo: adminData.photo || ''
    })

    // Charger les données de la firme via la nouvelle route dédiée
    try {
      const firmResponse = await api.auth.getFirmInfo()
      const firmData = firmResponse.data.data || firmResponse.data
      console.log('📊 Données Firm chargées:', firmData)

      // 🌟 FORMATE LES DATES DES VACANCES ICI 🌟
      const vacancesFormatees = (firmData.vacances || []).map(v => ({
        titre: v.titre || '',
        // On ne prend que les 10 premiers caractères (YYYY-MM-DD) si la date existe
        date_debut: v.date_debut ? v.date_debut.substring(0, 10) : '',
        date_fin: v.date_fin ? v.date_fin.substring(0, 10) : ''
      }))

      Object.assign(salonForm, {
        nomEnseigne: firmData.nom_societe || '',
        adresse: firmData.adresse || '',
        ville: firmData.ville || '',
        siret: firmData.siret || '',
        telephonePublic: firmData.telephone || '',
        horaires: firmData.horaires || {
          lundi: [],
          mardi: [],
          mercredi: [],
          jeudi: [],
          vendredi: [],
          samedi: [],
          dimanche: []
        },
        vacances: vacancesFormatees, // 🌟 On assigne le tableau formaté ici
        image: firmData.image || firmData.logo || '',
        photos: firmData.photos || [],
        description: firmData.description || '',
        prepaiement_active: firmData.prepaiement_active || false,
        acompte_demande_par_defaut: firmData.acompte_demande_par_defaut || false,
        prepaiement_optionnel: firmData.prepaiement_optionnel || false
      })

      console.log('✅ SalonForm après chargement:', salonForm)

      // Charger les paramètres SMS
      Object.assign(smsForm, {
        sendConfirmation: firmData.sms_confirmation_enabled !== undefined ? firmData.sms_confirmation_enabled : true,
        sendReminder: firmData.sms_rappel_enabled !== undefined ? firmData.sms_rappel_enabled : true,
        sendCancellation: firmData.sms_annulation_enabled !== undefined ? firmData.sms_annulation_enabled : false
      })
    } catch (firmError) {
      console.error('Erreur lors du chargement des données entreprise:', firmError)
      console.error('Détails de l\'erreur:', firmError.response?.data)
    }

  } catch (error) {
    console.error('Erreur lors du chargement des paramètres:', error)
    toast.error('Erreur lors du chargement des paramètres', {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isLoading.value = false
  }
}

const saveAllSettings = async () => {
  if (isSaving.value) return

  try {
    isSaving.value = true

    // Sauvegarder selon l'onglet actif
    if (activeTab.value === 'profile') {
      // Ne sauvegarder que le profil utilisateur
      await api.auth.updateProfile(accountForm)
    } else if (activeTab.value === 'salon' || activeTab.value === 'public-profile') {
      // Sauvegarder les paramètres du salon (nécessite les permissions admin)
      if (isAdmin.value) {
        await api.settings.updateFirmSettings(salonForm)
      } else {
        toast.error('Vous n\'avez pas les permissions pour modifier ces paramètres', {
          position: 'top-right',
          autoClose: 3000
        })
        return
      }
    } else if (activeTab.value === 'sms-config') {
      // Sauvegarder les paramètres SMS
      if (isAdmin.value) {
        await api.settings.updateSMSSettings(smsForm)
      }
    } else {
      // Pour les autres onglets, sauvegarder tout (par sécurité)
      await api.auth.updateProfile(accountForm)
      if (isAdmin.value) {
        await api.settings.updateFirmSettings(salonForm)
      }
    }

    toast.success('Paramètres sauvegardés avec succès !', {
      position: 'top-right',
      autoClose: 2000
    })

    // Recharger les données pour rafraîchir l'affichage
    await loadAllSettings()
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error)
    const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde'
    toast.error(errorMessage, {
      position: 'top-right',
      autoClose: 3000
    })
  } finally {
    isSaving.value = false
  }
}

const handleAvatarUpload = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Vérifier le type de fichier
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  if (!validTypes.includes(file.type)) {
    toast.error('Format non supporté. Utilisez JPEG, PNG ou WebP.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  // Vérifier la taille (max 5MB)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('La photo doit faire moins de 5 MB.', {
      position: 'top-right',
      autoClose: 3000
    })
    return
  }

  try {
    toast.info('Upload de la photo en cours...', {
      position: 'top-right',
      autoClose: 1000
    })

    const response = await api.settings.uploadProfilePhoto(file)

    if (response.data.success) {
      accountForm.photo = response.data.photoUrl
      toast.success('Photo de profil mise à jour !', {
        position: 'top-right',
        autoClose: 2000
      })
    }
  } catch (error) {
    console.error('Erreur upload photo:', error)
    toast.error('Erreur lors de l\'upload de la photo', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

// === Google Calendar Functions ===
const loadGoogleConnections = async () => {
  try {
    const response = await api.calendar.getConnections()
    connections.value = response.data.connections || []
  } catch (error) {
    console.error('Erreur lors du chargement des connexions:', error)
  }
}

const goToSyncPage = () => {
  router.push('/appointments/import')
}

// === Subscription Functions ===
const loadSubscriptionStatus = async () => {
  try {
    subscriptionLoading.value = true
    const response = await api.subscription.getSubscriptionStatus()
    
    // Correction ici : response.data.data.subscription
    if (response.data && response.data.data) {
      subscriptionStatus.value = response.data.data.subscription || subscriptionStatus.value
    }
    
  } catch (error) {
    console.error('Erreur lors du chargement de l\'abonnement:', error)
  } finally {
    subscriptionLoading.value = false
  }
}

const openStripePortal = async () => {
  try {
    isOpeningPortal.value = true
    const response = await api.subscription.createPortalSession()

    // Rediriger vers le portail Stripe
    window.location.href = response.data.url
  } catch (error) {
    console.error('Erreur lors de l\'ouverture du portail:', error)
    toast.error('Erreur lors de l\'ouverture du portail de gestion', {
      position: 'top-right',
      autoClose: 3000
    })
    isOpeningPortal.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

// === SMS Credits Functions ===
const loadSmsBalance = async () => {
  try {
    smsLoading.value = true
    const response = await api.subscription.getSmsBalance()
    console.log(response)
    smsBalance.value = {
      balance: response.data.balance || 0,
      totalPurchased: response.data.totalPurchased || 0,
      totalUsed: response.data.totalUsed || 0
    }

    // Charger aussi les paramètres de recharge auto
    if (response.data.autoRecharge !== undefined) {
      autoRechargeEnabled.value = response.data.autoRecharge
      autoRechargeAmount.value = response.data.autoRechargeAmount || 100
      autoRechargePackage.value = response.data.autoRechargePackage || 'small'
    }
  } catch (error) {
    console.error('Erreur lors du chargement du solde SMS:', error)
  } finally {
    smsLoading.value = false
  }
}

const toggleAutoRecharge = async () => {
  try {
    console.log('🔄 Mise à jour de la recharge auto:', autoRechargeEnabled.value)

    const response = await api.subscription.updateAutoRecharge(
      autoRechargeEnabled.value,
      autoRechargeAmount.value,
      autoRechargePackage.value
    )

    if (response.data.success) {
      toast.success(
        autoRechargeEnabled.value
          ? 'Recharge automatique activée ! 100 SMS seront rechargés automatiquement à 0 crédit.'
          : 'Recharge automatique désactivée',
        {
          position: 'top-right',
          autoClose: 3000
        }
      )
    }
  } catch (error) {
    console.error('Erreur lors de la mise à jour:', error)
    // Rétablir l'ancien état en cas d'erreur
    autoRechargeEnabled.value = !autoRechargeEnabled.value
    toast.error('Erreur lors de la mise à jour de la recharge automatique', {
      position: 'top-right',
      autoClose: 3000
    })
  }
}

// Sauvegarder les paramètres SMS automatiquement quand une checkbox change
const saveSmsSettings = async () => {
  try {
    // Sécurité Vue 3 : On extrait les données que ce soit une ref() ou un reactive()
    const formData = smsForm.value || smsForm;

    console.log('💾 Tentative de sauvegarde des paramètres SMS...', {
      sms_confirmation_enabled: formData.sendConfirmation,
      sms_rappel_enabled: formData.sendReminder,
      sms_annulation_enabled: formData.sendCancellation
    });

    const response = await api.settings.updateFirmSettings({
      sms_confirmation_enabled: formData.sendConfirmation,
      sms_rappel_enabled: formData.sendReminder,
      sms_annulation_enabled: formData.sendCancellation
    });
    console.log(response)

    toast.success('Paramètres SMS enregistrés', {
      position: 'top-right',
      autoClose: 2000
    });
  } catch (error) {
    console.error('Erreur lors de la sauvegarde des paramètres SMS:', error);
    toast.error('Erreur lors de la sauvegarde', {
      position: 'top-right',
      autoClose: 3000
    });
  }
}

const purchaseSmsPackage = async (packageType) => {
  try {
    isPurchasing.value = true
    console.log('💰 Achat du package:', packageType)

    const response = await api.subscription.purchaseSmsCredits(packageType)

    // Rediriger vers Stripe Checkout
    window.location.href = response.data.checkoutUrl
  } catch (error) {
    console.error('Erreur lors de l\'achat:', error)
    toast.error('Erreur lors de l\'achat de crédits SMS', {
      position: 'top-right',
      autoClose: 3000
    })
    isPurchasing.value = false
  }
}

const verifySmsPurchaseFromUrl = async () => {
  try {
    // Récupérer le sessionId depuis l'URL (Stripe le met dans l'URL de success)
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('session_id')

    if (!sessionId) {
      toast.success('Crédits SMS achetés avec succès !', {
        position: 'top-right',
        autoClose: 3000
      })
      await loadSmsBalance()
      // Nettoyer l'URL
      window.history.replaceState({}, '', '/settings')
      return
    }

    console.log('🔍 Vérification de l\'achat SMS avec session:', sessionId)
    const response = await api.subscription.verifySmsPurchase(sessionId)

    if (response.data.success) {
      const credits = response.data.creditsAdded || 0
      toast.success(`${credits} crédits SMS ajoutés avec succès ! Nouveau solde : ${response.data.newBalance}`, {
        position: 'top-right',
        autoClose: 4000
      })
    }

    await loadSmsBalance()

    // Nettoyer l'URL
    window.history.replaceState({}, '', '/settings')
  } catch (error) {
    console.error('❌ Erreur lors de la vérification de l\'achat:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la vérification du paiement. Contactez le support si les crédits n\'apparaissent pas.', {
      position: 'top-right',
      autoClose: 5000
    })
    // Nettoyer l'URL même en cas d'erreur
    window.history.replaceState({}, '', '/settings')
  }
}







import axios from 'axios'

const uploadLogo = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  const formData = new FormData()
  formData.append('image', file)

  try {
    const response = await axios.post(
      'https://bookmysalon-967a856b16b6.herokuapp.com/api/settings/upload-logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    )

    if (response.data.success) {
      salonForm.image = response.data.imageUrl
      toast.success('Photo principale mise à jour avec succès')
    }
  } catch (error) {
    console.error('Erreur upload logo:', error)
    toast.error('Erreur lors de l\'upload de la photo')
  }
}

const uploadPhoto = async (event) => {
  const file = event.target.files[0]
  if (!file) return

  if (salonForm.photos.length >= 4) {
    toast.warning('Maximum 4 photos autorisées')
    return
  }

  const formData = new FormData()
  formData.append('image', file)

  try {
    const response = await axios.post(
      'https://bookmysalon-967a856b16b6.herokuapp.com/api/settings/upload-gallery-photo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      }
    )

    if (response.data.success) {
      salonForm.photos.push(response.data.imageUrl)
      toast.success('Photo ajoutée à la galerie')
    }
  } catch (error) {
    console.error('Erreur upload photo:', error)
    toast.error('Erreur lors de l\'upload de la photo')
  }
}

const removePhoto = async (index) => {
  if (confirm('Voulez-vous supprimer cette photo ?')) {
    salonForm.photos.splice(index, 1)

    // Sauvegarder immédiatement les modifications
    try {
      await api.settings.updateFirmSettings({ photos: salonForm.photos })
      toast.success('Photo supprimée')
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      toast.error('Erreur lors de la suppression de la photo')
      // Recharger les données en cas d'erreur
      await loadAllSettings()
    }
  }
}

// === Referral Functions ===
const loadReferralInfo = async () => {
  try {
    referralLoading.value = true
    const response = await api.auth.getReferralInfo()

    if (response.data.success) {
      referralData.value = {
        referralCode: response.data.data.referralCode || '',
        referredBy: response.data.data.referredBy || null,
        referralStats: response.data.data.referralStats || {
          freeMonthsEarned: 0,
          freeMonthsUsed: 0,
          freeMonthsRemaining: 0
        },
        referredUsers: response.data.data.referredUsers || [],
        totalReferrals: response.data.data.totalReferrals || 0
      }
    }
  } catch (error) {
    console.error('Erreur lors du chargement des informations de parrainage:', error)
  } finally {
    referralLoading.value = false
  }
}

const copyReferralCode = () => {
  if (!referralData.value.referralCode) return

  navigator.clipboard.writeText(referralData.value.referralCode).then(() => {
    toast.success('Code copié dans le presse-papiers !', {
      position: 'top-right',
      autoClose: 2000
    })
  }).catch(err => {
    console.error('Erreur lors de la copie:', err)
    toast.error('Erreur lors de la copie du code', {
      position: 'top-right',
      autoClose: 2000
    })
  })
}

const shareReferralCode = (platform) => {
  const code = referralData.value.referralCode
  const message = `Rejoignez BookMySalon Pro avec mon code de parrainage ${code} et offrez-moi 1 mois gratuit !`

  let url = ''
  switch (platform) {
    case 'email':
      url = `mailto:?subject=Rejoignez BookMySalon Pro&body=${encodeURIComponent(message)}`
      break
    case 'whatsapp':
      url = `https://wa.me/?text=${encodeURIComponent(message)}`
      break
    case 'facebook':
      url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(message)}`
      break
    case 'twitter':
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`
      break
  }

  if (url) {
    window.open(url, '_blank')
  }
}

// === Stripe Connect Functions ===
const loadStripeConnectStatus = async () => {
  try {
    stripeConnectLoading.value = true
    const response = await api.auth.getFirmInfo()

    if (response.data && response.data.data) {
      const firm = response.data.data
      stripeConnectStatus.value = {
        connected: !!firm.stripe_account_id,
        accountId: firm.stripe_account_id || null
      }
    }

    console.log('Statut Stripe Connect:', stripeConnectStatus.value)
  } catch (error) {
    console.error('Erreur lors du chargement du statut Stripe:', error)
  } finally {
    stripeConnectLoading.value = false
  }
}

const createStripeAccount = async () => {
  try {
    isCreatingStripeAccount.value = true

    const response = await api.settings.createStripeAccount()

    if (response.data && response.data.onboardingUrl) {
      toast.success('Redirection vers Stripe pour finaliser la configuration...', {
        position: 'top-right',
        autoClose: 2000
      })

      // Rediriger vers l'onboarding Stripe après un court délai
      setTimeout(() => {
        window.location.href = response.data.onboardingUrl
      }, 500)
    }
  } catch (error) {
    console.error('Erreur lors de la création du compte Stripe:', error)

    // Si le compte existe déjà, recharger le statut
    if (error.response?.status === 400 && error.response?.data?.message?.includes('déjà créé')) {
      await loadStripeConnectStatus()
      toast.info('Votre compte Stripe est déjà connecté', {
        position: 'top-right',
        autoClose: 3000
      })
    } else {
      toast.error(error.response?.data?.message || 'Erreur lors de la connexion à Stripe', {
        position: 'top-right',
        autoClose: 4000
      })
    }

    isCreatingStripeAccount.value = false
  }
}

</script>
